import { useSyncExternalStore } from 'react';

import {
  acceptConsent,
  declineConsent,
  dismissConsentBanner,
  getConsentState,
  reopenConsentBanner,
  subscribeConsent,
  type ConsentState,
} from './consentStore';

export interface UseConsentResult extends ConsentState {
  accept: () => void;
  decline: () => void;
  /** "Not now" — hides the banner for this session and grants nothing. */
  dismiss: () => void;
  reopen: () => void;
}

/**
 * React view onto the consent singleton.
 *
 * `useSyncExternalStore` rather than context state: `gaPixel` / `metaPixel`
 * are plain modules with no provider in scope and need a synchronous read, so
 * the store has to live outside React. This hook makes React a subscriber to
 * that same store instead of a second copy of the truth.
 *
 * The server snapshot returns the same object as the client one — the store's
 * off-window branch resolves to withheld-with-no-banner, so there is no
 * hydration mismatch to guard against.
 */
export function useConsent(): UseConsentResult {
  const state = useSyncExternalStore(
    subscribeConsent,
    getConsentState,
    getConsentState,
  );
  return {
    ...state,
    accept: acceptConsent,
    decline: declineConsent,
    dismiss: dismissConsentBanner,
    reopen: reopenConsentBanner,
  };
}
